import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";

export default function DashboardLayout({ title, links, testIdPrefix }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const fullBleed = pathname === "/admin/inbox" || pathname.startsWith("/admin/inbox/");
  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "sales";
  // The Android app is a thin shell scoped to just the Command Center -- no
  // sidebar or cross-navigation into the rest of the admin panel or public site.
  const isNativeApp = Capacitor.isNativePlatform();
  const logo = (
    <div className="flex items-center gap-2">
      <img
        src="https://housedesignwala.com/api/files/18b1b263-2c1d-4631-8cfa-3c743bdb0809"
        alt="HouseDesignWala"
        className="w-14 h-14 rounded-full object-cover"
      />
      <div className="font-display font-extrabold text-[#1a3c5e] hidden sm:block">HouseDesignWala<span className="text-[#F7941D]">™</span></div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#FEF3E2]/30 flex flex-col">
      <header className="bg-white border-b border-[#1a3c5e]/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {isNativeApp ? logo : <NavLink to="/" className="flex items-center gap-2">{logo}</NavLink>}
          <div className="text-sm font-semibold text-[#1a3c5e] hidden md:block">{title}</div>
          <div className="flex items-center gap-2">
            {isAdmin && !isNativeApp && <GlobalSearch />}
            <div className="text-right hidden sm:block mr-1">
              <div className="text-xs font-semibold text-[#1a3c5e] truncate max-w-[140px]">{user?.name}</div>
              <div className="text-[10px] text-[#F7941D] uppercase tracking-wider font-bold">{user?.role}</div>
            </div>
            <NotificationBell testIdPrefix={testIdPrefix} />
            <button onClick={logout} data-testid={`${testIdPrefix}-logout`} className="p-2 rounded-lg text-[#1a3c5e] hover:bg-[#FEF3E2]" title="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className={`flex-1 w-full mx-auto py-6 grid lg:grid-cols-12 gap-6 ${fullBleed || isNativeApp ? "max-w-none px-2 sm:px-4 lg:px-4" : "max-w-7xl px-4 sm:px-6 lg:px-8"}`}>
        {!isNativeApp && (
          <aside className={`${fullBleed ? "lg:col-span-2" : "lg:col-span-3"} min-w-0`}>
            <nav className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-2 lg:sticky lg:top-20 flex lg:flex-col gap-1 overflow-x-auto scrollbar-thin max-w-full">
              {links.map((l, i) => {
                const prevGroup = i > 0 ? links[i - 1].group : undefined;
                const showGroupHeader = l.group && l.group !== prevGroup;
                return (
                  <React.Fragment key={l.to}>
                    {showGroupHeader && (
                      <div
                        className={`hidden lg:block px-3.5 text-[10px] font-bold uppercase tracking-wider text-[#1a3c5e]/40 ${i === 0 ? "pt-1 pb-1.5" : "pt-4 pb-1.5"}`}
                      >
                        {l.group}
                      </div>
                    )}
                    <NavLink
                      to={l.to}
                      end={l.end}
                      data-testid={`${testIdPrefix}-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                          isActive
                            ? "bg-[#F7941D] text-white shadow-sm"
                            : "text-[#1a3c5e]/80 hover:bg-[#FEF3E2]"
                        }`
                      }
                    >
                      <l.icon className="w-4 h-4 flex-shrink-0" />
                      {l.label}
                    </NavLink>
                  </React.Fragment>
                );
              })}
            </nav>
          </aside>
        )}
        <main className={`${isNativeApp ? "lg:col-span-12" : fullBleed ? "lg:col-span-10" : "lg:col-span-9"} space-y-5 min-w-0`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
